import { useState } from 'react'
import { useNavigate } from 'react-router'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { IconoActualizar, IconoConversaciones } from '../componentes/iconos'
import {
  useDerivaciones,
  useMarcarDerivacion,
  type Derivacion,
  type EstadoDerivacion,
} from '../hooks/useDerivaciones'

/**
 * Sección "Incidencias" (B.6). Bandeja de lo que el AGENTE derivó: peticiones
 * fuera de bono, incidencias y repartos especiales que el cliente/lead pidió por
 * chat. Cada derivación es una tarjeta con su motivo, resumen de la conversación
 * y el estado del correo. El equipo la marca como resuelta o la descarta.
 *
 * La tarjeta entera es clickeable y abre la conversación del cliente/lead
 * (Conversaciones prefiltrado por su teléfono). Los botones de acción no
 * disparan la navegación (stopPropagation).
 */

const ETIQUETA_CANAL: Record<string, string> = {
  clientes: 'Cliente',
  leads_castellon: 'Lead Castellón',
  leads_valencia: 'Lead Valencia',
}

function fmtFecha(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/** Ruta a la conversación del cliente/lead a partir del canal + teléfono. */
function enlaceChat(canal: string, telefono: string | null): string | null {
  if (!telefono) return null
  const tel = encodeURIComponent(telefono)
  if (canal === 'leads_castellon') return `/conversaciones?tipo=leads&zona=castellon&buscar=${tel}`
  if (canal === 'leads_valencia') return `/conversaciones?tipo=leads&zona=valencia&buscar=${tel}`
  return `/conversaciones?tipo=clientes&buscar=${tel}`
}

function ChipCanal({ canal }: { canal: string }) {
  const esLead = canal.startsWith('leads')
  return (
    <span
      className={[
        'shrink-0 rounded-md px-2 py-0.5 text-[10.5px] font-bold',
        esLead ? 'bg-negativo-fondo text-negativo-tinta' : 'bg-acento-suave text-acento-tinta',
      ].join(' ')}
    >
      {ETIQUETA_CANAL[canal] ?? canal}
    </span>
  )
}

function Tarjeta({
  d,
  onMarcar,
  ocupado,
  onAbrirChat,
}: {
  d: Derivacion
  onMarcar: (estado: EstadoDerivacion) => void
  ocupado: boolean
  onAbrirChat: (() => void) | null
}) {
  const gestionada = d.estado !== 'pendiente'
  const abrible = !!onAbrirChat
  return (
    <li
      {...(abrible
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: onAbrirChat,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onAbrirChat?.()
              }
            },
            'aria-label': `Abrir conversación de ${d.nombre ?? 'cliente'}`,
          }
        : {})}
      className={[
        'flex flex-col gap-2 rounded-xl border border-borde bg-superficie p-4',
        'border-l-4',
        d.estado === 'pendiente'
          ? 'border-l-atencion-anillo'
          : d.estado === 'gestionada'
            ? 'border-l-positivo-anillo'
            : 'border-l-borde-fuerte',
        gestionada ? 'opacity-70' : '',
        abrible
          ? 'cursor-pointer transition hover:-translate-y-px hover:border-acento hover:shadow-[inset_0_0_0_1px_var(--color-acento)] focus:outline-none focus-visible:border-acento focus-visible:shadow-[inset_0_0_0_1px_var(--color-acento)]'
          : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-bold text-tinta">{d.nombre ?? 'Sin nombre'}</span>
        <ChipCanal canal={d.canal} />
      </div>

      <p className="text-xs leading-snug text-tinta-2">{d.motivo}</p>

      {d.resumen ? (
        <p className="rounded-lg bg-elevado px-2.5 py-2 text-[11.5px] leading-relaxed text-tinta-apagada">
          {d.resumen}
        </p>
      ) : null}

      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="text-[10.5px] text-tinta-apagada">
          {fmtFecha(d.creadaEn)} · {d.tipoCorreo.replace(/_/g, ' ').toUpperCase()}
          {d.correoEnviado ? ' ✓' : ''}
        </span>
        {d.estado === 'pendiente' ? (
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              disabled={ocupado}
              onClick={(e) => {
                e.stopPropagation()
                onMarcar('gestionada')
              }}
              className="rounded-lg border border-positivo-anillo bg-positivo-fondo px-2.5 py-1 text-[11.5px] font-semibold text-positivo-tinta transition hover:brightness-110 disabled:opacity-50"
            >
              ✓ Marcar resuelta
            </button>
            <button
              type="button"
              disabled={ocupado}
              onClick={(e) => {
                e.stopPropagation()
                onMarcar('descartada')
              }}
              className="rounded-lg border border-borde-fuerte px-2.5 py-1 text-[11.5px] font-semibold text-tinta-apagada transition hover:bg-elevado disabled:opacity-50"
            >
              Descartar
            </button>
          </div>
        ) : (
          <span
            className={[
              'shrink-0 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold',
              d.estado === 'gestionada' ? 'bg-positivo-fondo text-positivo-tinta' : 'bg-elevado text-tinta-apagada',
            ].join(' ')}
          >
            {d.estado === 'gestionada' ? 'Resuelta' : 'Descartada'}
          </span>
        )}
      </div>

      {abrible ? (
        <span className="mt-0.5 flex items-center gap-1.5 text-[10.5px] font-medium text-acento-tinta opacity-80">
          <IconoConversaciones className="size-3.5" />
          Abrir conversación
        </span>
      ) : null}
    </li>
  )
}

const FILTROS: { clave: EstadoDerivacion | undefined; etiqueta: string }[] = [
  { clave: 'pendiente', etiqueta: 'Pendientes' },
  { clave: undefined, etiqueta: 'Todas' },
  { clave: 'gestionada', etiqueta: 'Resueltas' },
]

export function Incidencias({ sinCabecera = false }: { sinCabecera?: boolean }) {
  const [filtro, setFiltro] = useState<EstadoDerivacion | undefined>('pendiente')
  const derivaciones = useDerivaciones(filtro)
  const marcar = useMarcarDerivacion()
  const navegar = useNavigate()
  const items = derivaciones.data?.items ?? []

  return (
    <div className="flex flex-col gap-4">
      <div className={`flex items-start justify-between gap-3 ${sinCabecera ? 'sm:justify-end' : ''}`}>
        {sinCabecera ? null : (
          <div>
            <h1 className="text-xl font-semibold text-tinta">Incidencias</h1>
            <p className="mt-1 max-w-2xl text-sm text-tinta-apagada">
              Peticiones fuera de bono, incidencias y repartos especiales que el agente derivó
              desde el chat. Cada una ya generó su correo al área; aquí las gestionas.
            </p>
          </div>
        )}
        <button
          type="button"
          aria-label="Actualizar"
          onClick={() => void derivaciones.refetch()}
          className="flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
        >
          <IconoActualizar className="size-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      <div className="flex gap-2">
        {FILTROS.map((f) => (
          <button
            key={f.etiqueta}
            type="button"
            onClick={() => setFiltro(f.clave)}
            aria-pressed={filtro === f.clave}
            className={[
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
              filtro === f.clave
                ? 'bg-acento text-tinta-inversa'
                : 'border border-borde-fuerte text-tinta-2 hover:bg-elevado',
            ].join(' ')}
          >
            {f.etiqueta}
          </button>
        ))}
      </div>

      {derivaciones.isPending ? (
        <SkeletonList />
      ) : derivaciones.isError ? (
        <EstadoError error={derivaciones.error} onReintentar={() => void derivaciones.refetch()} />
      ) : items.length === 0 ? (
        <EstadoVacio
          titulo="No hay incidencias"
          descripcion={
            filtro === 'pendiente'
              ? 'Nada pendiente de gestionar. Cuando el agente derive algo, aparecerá aquí.'
              : 'No hay derivaciones con este filtro.'
          }
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((d) => {
            const destino = enlaceChat(d.canal, d.telefono)
            return (
              <Tarjeta
                key={d.id}
                d={d}
                ocupado={marcar.isPending && marcar.variables?.id === d.id}
                onMarcar={(estado) => marcar.mutate({ id: d.id, estado })}
                onAbrirChat={destino ? () => navegar(destino) : null}
              />
            )
          })}
        </ul>
      )}

      {marcar.isError ? (
        <p role="alert" className="text-sm text-critico-tinta">
          No se ha podido actualizar la incidencia. Inténtalo de nuevo.
        </p>
      ) : null}
    </div>
  )
}
